/*
  product-form.js – lightweight variant / price handler inspired by Shopify Dawn
  ------------------------------------------------------------------------
  Attach once per <form action="/cart/add"> that contains:
   – data-product-id            (number)
   – <script id="ProductJSON-{{ id }}"> with full product JSON
   – one hidden <input name="id"> (holds current variant id)
   – any option inputs bearing data-option-index (0-based) – either
       • <input type="radio"/> or
       • <select>
   – optional <select name="quantity"> for multiplying displayed price
   – price display nodes (any of):
       • .one-time-price
       • .subscription-discount-price, .one-subscription-price
   – optional .selling-plan-select wrapper / select
   – .current-product-image – main featured image to swap
   – .starter-kit-select img – thumbnail inside bundle block (if present)

  The script updates:
   • Variant id input
   • Price display nodes (multiplied by quantity if select present)
   • Selling-plan block visibility
   • Add to Cart button enable / disable (+ price text in quick-buy)
   • Images

  FORM SYNCHRONIZATION:
  Multiple forms for the same product (e.g., main product form and sticky form) 
  are automatically synchronized. When a user changes an option in one form, 
  all other forms for the same product will be updated to match. This includes:
   • Option selections (scent, color, etc.)
   • Starter kit vs refills selection
   • Selling plan selection (subscription vs one-time)
   • Quantity selection
   • Product data switching (main product vs refill product)
*/

class ProductForm {
  
   // Static registry to track all forms for the same product
   static formRegistry = new Map();
  
   constructor(form) {

      this.form = form;
      this.productId = form.dataset.productId;
      if (!this.productId) {
         //console.warn('ProductForm: No product ID found in form dataset' , this.form);
         return;
      }

      const jsonEl = document.getElementById(`ProductJSON-${this.productId}`);
      if (!jsonEl) {
         //console.warn('ProductForm: No product JSON element found for product ID:', this.form);
         return;
      }

      try {
         this.allProductData = JSON.parse(jsonEl.textContent);
         this.productData = this.allProductData; // Start with main product
         this.refillProductData = this.allProductData.refill_product || null;

         if (!this.productData || !this.productData.options || !Array.isArray(this.productData.options)) {
            return;
         } else {

         }
      } catch (error) {
         return;
      }

      // Get product handle from URL or product data
      this.productHandle = this.getProductHandle();
      
      // Define products that should have 450 cents deducted
      this.discountProducts = [ ];
      // Define products that should have 600 cents deducted
      this.discountProducts600 = []; // Add your 600-cent discount products here

      this.variantInput = form.querySelector('input[name="id"]');
      this.optionInputs = form.querySelectorAll('[data-option-index]');
      this.quantitySelect = form.querySelector('select[name="quantity"]');

      // Price nodes
      this.priceEls = form.querySelectorAll('.one-time-price');
      this.subscriptionEls = form.querySelectorAll('.subscription-discount-price, .one-subscription-price');

      this.sellingPlanWrapper = form.querySelector('.select-selling-plan, .selling-plans-wrapper');
      this.sellingPlanSelect = form.querySelector('select[name="selling_plan"]');

      this.addToCartButton = form.querySelector('button[type="submit"]');

      // Image nodes
      this.featuredImage = document.querySelector('.current-product-image'); // global gallery image
      this.colorSelectThumb = form.querySelector('.color-select img');

      // Refills/Starter Kit elements
      this.starterKitRadios = form.querySelectorAll('input[name="starter-kit-radio"], input[name="ever-bottle"]');
      this.colorSelect = form.querySelector('.color-select');
      this.refillsOnlyQuantity = form.querySelector('.refills-only-quantity, .refills-only-select');

      // Initialize syncing flag
      this.isSyncing = false;

      // Register this form instance
      this.registerForm();

      // Bind events
      form.addEventListener('change', (e) => {
         if (e.target.matches('[data-option-index], [name="quantity"], select[name="selling_plan"], input[name="starter-kit-radio"], input[name="ever-bottle"]')) {
            this.onChange();
            // Sync changes to other forms (only if not already syncing)
            if (!this.isSyncing) {
               this.syncToOtherForms();
            }
         }
      });

      // Add specific event listener for selling plan changes
      if (this.sellingPlanSelect) {
         this.sellingPlanSelect.addEventListener('change', () => {
            this.onChange();
            if (!this.isSyncing) {
               this.syncToOtherForms();
            }
         });
      }

      // Add event listener for nested selling plan select (inside subscription radio)
      const nestedSellingPlanSelect = this.form.querySelector('label select[name="selling_plan"]');
      if (nestedSellingPlanSelect && nestedSellingPlanSelect !== this.sellingPlanSelect) {
         nestedSellingPlanSelect.addEventListener('change', () => {
            this.onChange();
            if (!this.isSyncing) {
               this.syncToOtherForms();
            }
         });
      }

      // Add event listeners for selling plan radio buttons (regular view)
      const sellingPlanRadios = form.querySelectorAll('input[type="radio"][name*="selling-plan"]');
      if (sellingPlanRadios.length > 0) {
         sellingPlanRadios.forEach(radio => {
            radio.addEventListener('change', () => {
               this.onChange();
               if (!this.isSyncing) {
                  this.syncToOtherForms();
               }
            });
         });
      }

      // Add event listeners for ever-bottle radios
      if (this.starterKitRadios.length > 0) {
         this.starterKitRadios.forEach(radio => {
         radio.addEventListener('change', (e) => {
            this.handleEverBottleChange(e);
            if (!this.isSyncing) {
               this.syncToOtherForms();
            }
         });
         });
      }

      // Add event listener for subscription radio
      const subscriptionRadios = form.querySelectorAll('input[type="radio"]');
      if (subscriptionRadios.length > 1) {
         const subscriptionRadio = subscriptionRadios[1]; // Second radio is subscription
         subscriptionRadio.addEventListener('change', () => {
         if (subscriptionRadio.checked) {
            // Ensure selling plan select is visible and has a value
            if (this.sellingPlanSelect) {
               this.sellingPlanSelect.classList.remove('hidden');
               if (this.sellingPlanSelect.value === '') {
               this.sellingPlanSelect.value = this.sellingPlanSelect.options[0].value;
               this.sellingPlanSelect.dispatchEvent(new Event('change'));
               }
            }
         }
         });
      }

      // Initial render
      this.onChange();
   }

   /* Register this form instance in the static registry */
   registerForm() {
      if (!ProductForm.formRegistry.has(this.productId)) {
         ProductForm.formRegistry.set(this.productId, []);
      }
      ProductForm.formRegistry.get(this.productId).push(this);
      
      // Debug logging
      const totalForms = ProductForm.formRegistry.get(this.productId).length;
      console.log(`ProductForm: Registered form for product ${this.productId}. Total forms: ${totalForms}`);
   }

   /* Remove this form instance from the static registry */
   unregisterForm() {
      const forms = ProductForm.formRegistry.get(this.productId);
      if (forms) {
         const index = forms.indexOf(this);
         if (index > -1) {
            forms.splice(index, 1);
         }
         if (forms.length === 0) {
            ProductForm.formRegistry.delete(this.productId);
         }
      }
   }

   /* Get other forms for the same product */
   getOtherForms() {
      const forms = ProductForm.formRegistry.get(this.productId) || [];
      return forms.filter(form => form !== this && form.form && form.form.isConnected);
   }

   /* Sync current form state to other forms */
   syncToOtherForms() {
      const otherForms = this.getOtherForms();
      if (otherForms.length === 0) return;
      
      // Debug logging
      console.log(`ProductForm: Syncing to ${otherForms.length} other forms for product ${this.productId}`);

      const selectedOptions = this.getSelectedOptions();
      const currentVariant = this.findVariant(selectedOptions);
      const isStarterKit = this.isStarterKitSelected();
      const isOneTime = this.isOneTimePurchaseSelected();
      const selectedSellingPlan = this.getSelectedSellingPlanValue();
      const quantity = this.quantitySelect ? parseInt(this.quantitySelect.value, 10) || 1 : 1;

      otherForms.forEach(otherForm => {
         otherForm.syncFromOtherForm({
            selectedOptions,
            currentVariant,
            isStarterKit,
            isOneTime,
            selectedSellingPlan,
            quantity,
            productData: this.productData // Pass the current product data
         });
      });
   }

   /* Sync state from another form */
   syncFromOtherForm(syncData) {
      const { selectedOptions, currentVariant, isStarterKit, isRefillsOnly, isOneTime, selectedSellingPlan, quantity, productData } = syncData;

      // Set a flag to prevent infinite loops during sync
      this.isSyncing = true;
      
      // Handle product data switching if needed
      if (productData && productData !== this.productData) {
         if (productData === this.refillProductData) {
            // Switch to refill product
            this.productData = this.refillProductData;
            this.form.dataset.productId = this.refillProductData.id;
            this.updateProductHandle();
         } else if (productData === this.allProductData) {
            // Switch to main product
            this.productData = this.allProductData;
            this.form.dataset.productId = this.allProductData.id;
            this.updateProductHandle();
         }
      }
      
      // Update variant input
      if (this.variantInput && currentVariant) {
         this.variantInput.value = currentVariant.id;
      }

      // Update option inputs based on the current product data (main or refill)
      this.optionInputs.forEach((input) => {
         const idx = parseInt(input.dataset.optionIndex, 10);
         if (isNaN(idx) || !selectedOptions[idx]) return;
         
         if (input.tagName === 'INPUT' && input.type === 'radio') {
            input.checked = input.value === selectedOptions[idx];
         } else if (input.tagName === 'SELECT') {
            input.value = selectedOptions[idx];
         }
      });

      // Update starter kit selection
      if (this.starterKitRadios.length > 0) {
         this.starterKitRadios.forEach(radio => {
            radio.checked = (radio.value === 'yes') === isStarterKit;
         });
      }



      // Update selling plan selection
      if (this.sellingPlanSelect) {
         if (isOneTime) {
            this.sellingPlanSelect.value = 'one-time';
            // console.log('Syncing: Set selling plan to one-time');
         } else if (selectedSellingPlan) {
            // Check if the selected plan exists in the current options
            const planExists = Array.from(this.sellingPlanSelect.options).some(option => 
               option.value === selectedSellingPlan
            );
            if (planExists) {
               this.sellingPlanSelect.value = selectedSellingPlan;
               // console.log('Syncing: Set selling plan to specific plan:', selectedSellingPlan);
            } else {
               // If the plan doesn't exist, select the first subscription option
               const subscriptionOptions = Array.from(this.sellingPlanSelect.options).filter(option => 
                  option.value !== 'one-time' && option.value !== ''
               );
               if (subscriptionOptions.length > 0) {
                  this.sellingPlanSelect.value = subscriptionOptions[0].value;
                  // console.log('Syncing: Set selling plan to first subscription option (plan not found):', subscriptionOptions[0].value);
               }
            }
         } else {
            // If subscription is selected but no specific plan, select the first subscription option
            const subscriptionOptions = Array.from(this.sellingPlanSelect.options).filter(option => 
               option.value !== 'one-time' && option.value !== ''
            );
            if (subscriptionOptions.length > 0) {
               this.sellingPlanSelect.value = subscriptionOptions[0].value;
               // console.log('Syncing: Set selling plan to first subscription option (no plan specified):', subscriptionOptions[0].value);
            }
         }
      }

      // Update selling plan radio buttons
      const sellingPlanRadios = this.form.querySelectorAll('input[type="radio"][name*="selling-plan"]');
      if (sellingPlanRadios.length > 0) {
         const radioArray = Array.from(sellingPlanRadios);
         if (radioArray.length >= 2) {
            const subscriptionRadio = radioArray[0]; // First radio is subscription
            const oneTimeRadio = radioArray[1]; // Second radio is one-time
            subscriptionRadio.checked = !isOneTime;
            oneTimeRadio.checked = isOneTime;
            
            // If subscription is selected, also update the nested selling plan select if it exists
            if (!isOneTime && selectedSellingPlan) {
               const nestedSellingPlanSelect = this.form.querySelector('label select[name="selling_plan"]');
               if (nestedSellingPlanSelect) {
                  nestedSellingPlanSelect.value = selectedSellingPlan;
               }
            }
         }
      }

      // Update quantity
      if (this.quantitySelect) {
         this.quantitySelect.value = quantity;
      }

      // Update refills quantity if applicable
      if (this.refillsOnlyQuantity) {
         const refillQuantitySelect = this.refillsOnlyQuantity.querySelector('select[name="quantity"]');
         if (refillQuantitySelect) {
            refillQuantitySelect.value = quantity;
         }
      }

      // Update UI state based on starter kit selection
      this.updateUIState(isStarterKit);

      // Clear the syncing flag
      this.isSyncing = false;

      // Update the form state
      this.onChange();
   }

   /* Return an array of selected option values (by option index) */
   getSelectedOptions() {
      if (!this.productData || !this.productData.options || !Array.isArray(this.productData.options)) {
         // console.warn('ProductForm: No valid product data available');
         return [];
      }

      const selected = Array(this.productData.options.length).fill(null);
      
      // For refill product, we need to handle options differently
      const isRefillProduct = this.productData === this.refillProductData;
      
      this.optionInputs.forEach((input) => {
         const idx = parseInt(input.dataset.optionIndex, 10);
         if (isNaN(idx)) return;
         
         // For radio inputs, only get the checked one
         if (input.tagName === 'INPUT' && input.type === 'radio') {
         if (input.checked) {
            selected[idx] = input.value;
         }
         } 
         // For select inputs, get the selected value
         else if (input.tagName === 'SELECT') {
         selected[idx] = input.value;
         }
      });

      // For refill product, if we have a scent in the second position, move it to first
      if (isRefillProduct && selected[1] && !selected[0]) {
         selected[0] = selected[1];
         selected[1] = null;
      }
      
      return selected;
   }

   /* Find variant that matches every non-null selected option value */
   findVariant(selectedOptions) {

      // For refill product, we need to match based on scent (first option)
      const isRefillProduct = this.productData === this.refillProductData;
      
      if (isRefillProduct && selectedOptions[0]) {
         //console.log('findVariant - Looking for refill variant with scent:', selectedOptions[0]);
         return this.productData.variants.find(variant => variant.options[0] === selectedOptions[0]);
      }
      
      // For main product, match all selected options
      return this.productData.variants.find((variant) => {
         // If first option is null but second option matches, that's a valid match
         if (selectedOptions[0] === null && selectedOptions[1] && variant.options[1] === selectedOptions[1]) {
         return true;
         }
         
         // Otherwise match all non-null selected options
         return selectedOptions.every((val, idx) => {
         if (!val) return true; // ignore not selected
         return variant.options[idx] === val;
         });
      });
   }

   /* Find variant in a specific product that matches selected options */
   findVariantInProduct(selectedOptions, productData) {
      return productData.variants.find((variant) => {
         return selectedOptions.every((val, idx) => {
         if (!val) return true; // ignore not selected
         return variant.options[idx] === val;
         });
      });
   }

   /* Get product handle from URL or product data */
   getProductHandle() {
      // Try to get from product data first
      if (this.productData && this.productData.handle) {
         return this.productData.handle;
      }
      
      // Fallback to URL path
      const path = window.location.pathname;
      const match = path.match(/\/products\/([^\/]+)/);
      return match ? match[1] : null;
   }

   /* Update product handle when switching between products */
   updateProductHandle() {
      this.productHandle = this.getProductHandle();
   }

   /* Apply discount to price if product is in discount list and conditions are met */
   applyDiscount(priceInCents, isSubscription = false) {

      if (this.productHandle && isSubscription && this.isStarterKitSelected()) {
         // Get current currency
         const currentCurrency = window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : 'EUR';
         
         // Check for 600-cent discount first (higher priority) - convert from EUR
         if (this.discountProducts600.includes(this.productHandle)) {
            const discountAmount = CurrencyUtils.convertFromEUR(600, currentCurrency);
            return Math.max(0, priceInCents - discountAmount); // Deduct converted amount, minimum 0
         }
         // Check for 450-cent discount - convert from EUR
         else if (this.discountProducts.includes(this.productHandle)) {
            const discountAmount = CurrencyUtils.convertFromEUR(450, currentCurrency);
            return Math.max(0, priceInCents - discountAmount); // Deduct converted amount, minimum 0
         }
      }
      return priceInCents;
   }

   /* Check if starter kit is selected (not refills only) */
   isStarterKitSelected() {
      // Check if any starter kit radio is checked with value 'yes' (handle both name patterns)
      const starterKitRadio = this.form.querySelector('input[name="starter-kit-radio"][value="yes"], input[name="ever-bottle"][value="yes"]');
      const allstarterKitRadios = this.form.querySelectorAll('input[name="starter-kit-radio"], input[name="ever-bottle"]');
      // If no starter kit radios exist, assume it's a starter kit (for products without this option)
      if (allstarterKitRadios.length === 0) {
         return true;
      }
      const isSelected = starterKitRadio && starterKitRadio.checked;
      return isSelected;
   }

   /* Update UI elements based on starter kit vs refills selection */
   updateUIState(isStarterKit) {
      // Update color selector visibility
      if (this.colorSelect) {
         this.colorSelect.classList.toggle('hidden', !isStarterKit);
         this.colorSelect.style.display = isStarterKit ? '' : 'none';
      }
      
      // Update refills quantity selector visibility
      if (this.refillsOnlyQuantity) {
         this.refillsOnlyQuantity.classList.toggle('hidden', isStarterKit);
         this.refillsOnlyQuantity.style.display = isStarterKit ? 'none' : '';
      }
   }

   /* Update perks display based on starter kit vs refills selection */
   updatePerksDisplay() {
      const starterKitPerks = this.form.querySelector('.starter-kit-perks');
      const refillsOnlyPerks = this.form.querySelector('.refills-only-perks');
      
      if (!starterKitPerks && !refillsOnlyPerks) {
         return; // No perks to update
      }

      const isStarterKit = this.isStarterKitSelected();
      
      if (isStarterKit) {
         // Starter Kit selected
         if (starterKitPerks) starterKitPerks.classList.remove('hidden');
         if (refillsOnlyPerks) refillsOnlyPerks.classList.add('hidden');
      } else {
         // Refills Only selected
         if (starterKitPerks) starterKitPerks.classList.add('hidden');
         if (refillsOnlyPerks) refillsOnlyPerks.classList.remove('hidden');
      }
   }

   /* Check if one-time purchase is selected (works for both radio buttons and select dropdown) */
   isOneTimePurchaseSelected() {
      // Check for compact view (select dropdown)
      if (this.sellingPlanSelect && this.sellingPlanSelect.dataset.view === 'compact') {
         const isOneTime = this.sellingPlanSelect.value === 'one-time';
         return isOneTime;
      }
      
      // Check for regular view (radio buttons)
      const sellingPlanRadios = this.form.querySelectorAll('input[type="radio"][name*="selling-plan"]');
      
      if (sellingPlanRadios.length > 0) {
         // Find the radio button that represents one-time purchase
         // It's the one with empty value that's NOT the first one (subscription is first, one-time is second)
         const radioArray = Array.from(sellingPlanRadios);
         if (radioArray.length >= 2) {
            const oneTimeRadio = radioArray[1]; // Second radio is one-time purchase
            if (oneTimeRadio && oneTimeRadio.checked) {
               return true;
            }
         }
      }
      
      return false;
   }

   /* Get the selected selling plan value (works for both radio buttons and select dropdown) */
   getSelectedSellingPlanValue() {
      // For compact view (select dropdown)
      if (this.sellingPlanSelect && this.sellingPlanSelect.dataset.view === 'compact') {
         // Only return the value if it's not 'one-time'
         if (this.sellingPlanSelect.value && this.sellingPlanSelect.value !== 'one-time') {
            // console.log('getSelectedSellingPlanValue - compact view:', this.sellingPlanSelect.value);
            return this.sellingPlanSelect.value;
         }
         // console.log('getSelectedSellingPlanValue - compact view: empty (one-time or no value)');
         return '';
      }
      
      // For regular view (radio buttons)
      const sellingPlanRadios = this.form.querySelectorAll('input[type="radio"][name*="selling-plan"]');
      if (sellingPlanRadios.length > 0) {
         // Find the checked radio button that represents subscription (first radio)
         const radioArray = Array.from(sellingPlanRadios);
         if (radioArray.length >= 2) {
            const subscriptionRadio = radioArray[0]; // First radio is subscription
            if (subscriptionRadio && subscriptionRadio.checked) {
               // Get the value from the selling plan select inside the subscription label
               const sellingPlanSelect = this.form.querySelector('label select[name="selling_plan"]');
               if (sellingPlanSelect && sellingPlanSelect.value) {
                  // console.log('getSelectedSellingPlanValue - regular view:', sellingPlanSelect.value);
                  return sellingPlanSelect.value;
               }
               // console.log('getSelectedSellingPlanValue - regular view: no value in nested select');
            }
         }
      }
      
      // console.log('getSelectedSellingPlanValue - returning empty');
      return '';
   }

   onChange() {
      const selectedOptions = this.getSelectedOptions();
      
      // Find the variant in the current product data
      let variant = this.findVariant(selectedOptions);
      
      // If no variant found, use the first available variant
      if (!variant) {
         variant = this.productData.variants[0];
         
         // Update the scent selection to match the first variant
         const scentInputs = this.form.querySelectorAll('[data-option-index="0"]');
         scentInputs.forEach(input => {
         if (input.tagName === 'INPUT' && input.type === 'radio') {
            input.checked = input.value === variant.options[0];
         } else if (input.tagName === 'SELECT') {
            input.value = variant.options[0];
         }
         });
      }

      // 1. Update hidden input
      if (this.variantInput) {
         this.variantInput.value = variant.id;
      }

      // Push variant id to dataLayer on PDP (initial render and on change)
      if (document.body && document.body.classList.contains('product') && !this.form.closest('.quick-buy')) {
         window.dataLayer = window.dataLayer || [];
         window.dataLayer.push({ event: 'variant_id', id: variant.id });
      }

      // 2. Prices (multiply if quantity select exists)
      // For refills, get quantity from the refills-only-quantity, otherwise use main quantity select
      let qty = 1;
      if (this.productData === this.refillProductData && this.refillsOnlyQuantity) {
         const refillQuantitySelect = this.refillsOnlyQuantity.querySelector('select[name="quantity"]');
         qty = refillQuantitySelect ? parseInt(refillQuantitySelect.value, 10) || 1 : 1;
      } else {
         qty = this.quantitySelect ? parseInt(this.quantitySelect.value, 10) || 1 : 1;
      }
      const oneTimePriceInCents = variant.price * qty;
      const discountedOneTimePriceInCents = this.applyDiscount(oneTimePriceInCents, false); // false = not subscription
      const oneTimePrice = discountedOneTimePriceInCents / 100;
      
      this.priceEls.forEach((el) => {
         el.textContent = `${CurrencyUtils.formatMoney(discountedOneTimePriceInCents)}`;
      });
      
      if (variant.selling_plan_allocations?.length) {
         const subPriceInCents = variant.selling_plan_allocations[0].price * qty;

         const discountedSubPriceInCents = this.applyDiscount(subPriceInCents, true); // true = subscription
         const subPrice = discountedSubPriceInCents / 100;
         this.subscriptionEls.forEach((el) => {
         el.textContent = `${CurrencyUtils.formatMoney(discountedSubPriceInCents)}`;
         });
      }

      // Update original price display
      const originalPriceEls = this.form.querySelectorAll('.original-price');
      if (originalPriceEls.length > 0) {
         // Use compare_at_price if available, otherwise fall back to regular price
         const priceToUse = variant.compare_at_price && variant.compare_at_price > variant.price ? variant.compare_at_price : variant.price;
         const originalPrice = (priceToUse / 100) * qty;
         originalPriceEls.forEach((el) => {
         el.textContent = `${CurrencyUtils.formatMoney(originalPrice*100)}`;
         });
      }

      // 3. Update perks display based on starter kit vs refills selection
      this.updatePerksDisplay();

      // 4. Selling plan visibility and options
      if (this.sellingPlanWrapper) {
         if (this.sellingPlanSelect && variant.selling_plan_allocations?.length && this.productData.selling_plan_groups) {
         // Store the currently selected selling plan
         const currentSelectedPlan = this.sellingPlanSelect.value;
         
         // Clear existing options
         this.sellingPlanSelect.innerHTML = '';
         
         // Add new options based on the variant's selling plans
         variant.selling_plan_allocations.forEach(allocation => {
            // Search through all selling plan groups to find the matching plan
            let sellingPlan = null;
            if (this.productData.selling_plan_groups) {
               for (const group of this.productData.selling_plan_groups) {
               sellingPlan = group.selling_plans.find(plan => plan.id === allocation.selling_plan_id);
               if (sellingPlan) break;
               }
            }
            
            // Only add option if it's a Firmhouse Plan
            if (sellingPlan && 
               sellingPlan.options[0].name != 'Recharge Plan ID' && 
               !sellingPlan.name.includes('(SEPA)')) {
               //console.log('sellingPlan', sellingPlan);
              const option = document.createElement('option');
              option.value = allocation.selling_plan_id;
              option.textContent = sellingPlan.name + (String(allocation.selling_plan_id) === '691592036698' ? ' (most popular)' : '');
               this.sellingPlanSelect.appendChild(option);
               
            }
         });

         // Add one-time purchase option only in compact view
         if (this.sellingPlanSelect.dataset.view === 'compact') {
            const oneTimeOption = document.createElement('option');
            oneTimeOption.value = 'one-time';
            oneTimeOption.textContent = 'One-time purchase';
            this.sellingPlanSelect.appendChild(oneTimeOption);
         }

        // Restore the previously selected plan if it exists in the new options
        const preferredPlanId = '691592036698';
        const optionsArray = Array.from(this.sellingPlanSelect.options);
        const hasCurrent = currentSelectedPlan && optionsArray.some(opt => opt.value === String(currentSelectedPlan));
        const preferredOption = optionsArray.find(opt => opt.value === preferredPlanId);

        if (hasCurrent) {
           this.sellingPlanSelect.value = String(currentSelectedPlan);
        } else if (preferredOption) {
           // Default to preferred plan on load when no previous selection
           this.sellingPlanSelect.value = preferredPlanId;
        } else if (this.sellingPlanSelect.options.length > 0) {
           // Fallback: select the first available subscription option
           this.sellingPlanSelect.value = this.sellingPlanSelect.options[0].value;
        }

         // Handle selling plan input for both compact view (select) and regular view (radio buttons)
         const isOneTimeSelected = this.isOneTimePurchaseSelected();
         
         if (isOneTimeSelected) {
            // Remove ALL selling_plan inputs for one-time purchases
            const existingSellingPlanInputs = this.form.querySelectorAll('input[name="selling_plan"]');
            existingSellingPlanInputs.forEach(input => {
               input.remove();
            });
            
            // For compact view, don't clear the selling plan select value when one-time is selected
            // This prevents the selection from being lost
            if (!this.sellingPlanSelect || this.sellingPlanSelect.dataset.view !== 'compact') {
               // Only clear selling_plan select dropdowns for regular view
               const sellingPlanSelects = this.form.querySelectorAll('select[name="selling_plan"]');
               sellingPlanSelects.forEach(select => {
                  if (select.value !== '') {
                     select.value = '';
                  }
               });
            }
         } else {
            // Ensure selling_plan input exists with the correct value
            let sellingPlanInput = this.form.querySelector('input[name="selling_plan"]');
            if (!sellingPlanInput) {
               sellingPlanInput = document.createElement('input');
               sellingPlanInput.type = 'hidden';
               sellingPlanInput.name = 'selling_plan';
               this.form.appendChild(sellingPlanInput);
            }
            
            // Get the selected selling plan value
            const selectedPlanValue = this.getSelectedSellingPlanValue();
            sellingPlanInput.value = selectedPlanValue;
         }

         // if (this.sellingPlanSelect.dataset.view === 'compact') {
         //    this.sellingPlanSelect.selectedIndex = 1;
         // }

         this.sellingPlanWrapper.classList.remove('hidden');
         } else {
         this.sellingPlanWrapper.classList.add('hidden');
         }
      }

      // 4. Add-to-Cart button state + (quick-buy price text)
      if (this.addToCartButton) {
         this.addToCartButton.disabled = !variant.available;
         if (!variant.available) {
            this.addToCartButton.textContent = 'Sold Out';
         } else {
            if (this.form.closest('.quick-buy')) {
               // Check if a subscription is selected (works for both compact and regular view)
               const isSubscription = !this.isOneTimePurchaseSelected();
               
               let priceInCents;
               if (isSubscription && variant.selling_plan_allocations?.length) {
                  priceInCents = variant.selling_plan_allocations[0].price * qty;
               } else {
                  priceInCents = variant.price * qty;
               }
               const discountedPriceInCents = this.applyDiscount(priceInCents, isSubscription);
               this.addToCartButton.textContent = `Add to cart • ${CurrencyUtils.formatMoney(discountedPriceInCents)}`;
            } else {
               this.addToCartButton.textContent = 'Add to cart';
            }
         }
      }

      // 5. Images
      if (variant.featured_image || variant.image) {
         const src = (variant.featured_image?.src || variant.image?.src)?.replace(/\{width\}/, '600');
         if (src) {
         // Only update images if this is not a compact view form
         if (!this.form.closest('.quick-buy-form')) {
            if (this.featuredImage) {
               this.featuredImage.src = src;
               this.featuredImage.alt = variant.title;
            }
         }
            if (this.colorSelectThumb) {
               this.colorSelectThumb.src = src;
               this.colorSelectThumb.alt = variant.title;
            }
         }
      }

      // 6. Update selected color display
      this.updateSelectedColorDisplay();
   }

   /* Update the selected color display text */
   updateSelectedColorDisplay() {
      const selectedColorElement = this.form.querySelector('.selectedColor');
      if (!selectedColorElement) return;

      // Find the currently selected color option
      const selectedColorOption = this.form.querySelector('.color-option:checked');
      if (selectedColorOption) {
         selectedColorElement.textContent = selectedColorOption.value;
      }
   }

   handleEverBottleChange(e) {
      const isStarterKit = e.target.value === 'yes';
      
      // Helper function to find scent option index
      const findScentOptionIndex = (productData) => {
         if (!productData || !productData.options || !Array.isArray(productData.options)) {
         return -1;
         }
         
         const index = productData.options.findIndex(option => {
         if (!option || typeof option !== 'object') return false;
         const name = option.name?.toLowerCase();
         return name === 'scent' || name === 'fragrance';
         });
         
         return index;
      };
      
      // Switch between main product and refill product data
      if (!isStarterKit && this.refillProductData) {
         // Switch to refill product
         this.productData = this.refillProductData;
         this.form.dataset.productId = this.refillProductData.id;
         this.updateProductHandle(); // Update product handle for discount logic
         
         // Get all selected options from the main product
         const selectedOptions = this.getSelectedOptions();
         // Find scent option index in main product
         const mainScentIndex = findScentOptionIndex(this.allProductData);
         // Get the selected scent from the main product's options
         const selectedScent = selectedOptions[mainScentIndex];
         // Find scent option index in refill product
         const refillScentIndex = findScentOptionIndex(this.refillProductData);
         // Find matching variant in refill product based on scent
         let matchingVariant = null;
         if (selectedScent && refillScentIndex !== -1) {
            matchingVariant = this.refillProductData.variants.find(variant => {
               return variant.options[refillScentIndex] === selectedScent;
            });
         }
         
         // Update the variant input to the matching refill product variant
         if (matchingVariant && this.variantInput) {
         this.variantInput.value = matchingVariant.id;
         
         // Update the scent selection in the refill product to match
         const refillScentInputs = this.form.querySelectorAll(`[data-option-index="${refillScentIndex}"]`);
         refillScentInputs.forEach(input => {
            if (input.tagName === 'INPUT' && input.type === 'radio') {
               input.checked = input.value === selectedScent;
            } else if (input.tagName === 'SELECT') {
               input.value = selectedScent;
            }
         });
         } else if (this.refillProductData.variants.length > 0) {
         this.variantInput.value = this.refillProductData.variants[0].id;
         }
         
      } else {
         // Switch back to main product
         this.productData = this.allProductData;
         this.form.dataset.productId = this.allProductData.id;
         this.updateProductHandle(); // Update product handle for discount logic
         
         // Get all selected options from the refill product
         const selectedOptions = this.getSelectedOptions();
         
         // Find scent option index in refill product
         const refillScentIndex = findScentOptionIndex(this.refillProductData);
         
         // Get the selected scent from the refill product's options
         const selectedScent = selectedOptions[refillScentIndex];
         
         // Find scent option index in main product
         const mainScentIndex = findScentOptionIndex(this.allProductData);
         
         // Find matching variant in main product based on scent
         let matchingVariant = null;
         if (selectedScent && mainScentIndex !== -1) {
         matchingVariant = this.allProductData.variants.find(variant => {
            return variant.options[mainScentIndex] === selectedScent;
         });
         }
         
         
         // Update the variant input to the matching main product variant
         if (matchingVariant && this.variantInput) {
         //console.log('handleEverBottleChange - Updating variant input to matching main variant:', matchingVariant.id);
         this.variantInput.value = matchingVariant.id;
         
         // Update all option selections in the main product to match the found variant
         this.allProductData.options.forEach((option, index) => {
            const optionInputs = this.form.querySelectorAll(`[data-option-index="${index}"]`);
            optionInputs.forEach(input => {
               if (input.tagName === 'INPUT' && input.type === 'radio') {
               input.checked = input.value === matchingVariant.options[index];
               } else if (input.tagName === 'SELECT') {
               input.value = matchingVariant.options[index];
               }
            });
         });
         } else if (this.allProductData.variants.length > 0) {
         //console.log('handleEverBottleChange - No matching variant found, using first main variant:', this.allProductData.variants[0].id);
         this.variantInput.value = this.allProductData.variants[0].id;
         }
         
      }
      
      // Update UI state based on starter kit selection
      this.updateUIState(isStarterKit);
      
      // If starter kit is selected, set quantity to 1 and update prices
      if (isStarterKit && this.refillsOnlyQuantity) {
         const quantitySelect = this.refillsOnlyQuantity.querySelector('select[name="quantity"]');
         if (quantitySelect) {
         quantitySelect.value = '1';
         // Update the quantity input for the cart
         const quantityInput = this.form.querySelector('input[name="quantity"]');
         if (quantityInput) {
            quantityInput.value = 1;
         }
         }
      } else if (!isStarterKit && this.refillsOnlyQuantity) {
         // When switching to refills, ensure the quantity selector has a value
         const quantitySelect = this.refillsOnlyQuantity.querySelector('select[name="quantity"]');
         if (quantitySelect && quantitySelect.value === '') {
            quantitySelect.value = '1';
         }
      }
      
      // Update perks display immediately
      this.updatePerksDisplay();
      
      // Trigger onChange to update prices and other elements
      this.onChange();
   }
   }

// Auto-init for every form on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('form[action="/cart/add"]').forEach((form) => new ProductForm(form));
}); 